import React, { useEffect, useState, useCallback, useRef, memo } from 'react';
import DataGrid, { Column, LoadPanel, Lookup, Editing } from 'devextreme-react/data-grid';
import Popup, { ToolbarItem } from 'devextreme-react/popup';
import { LoadIndicator } from 'devextreme-react';
import { useScreenSize } from '../../../../utils/media-query';
import { useApiConfigUsuarios } from '../../../../api/api.ConfigUsuarios';
import { newToast } from '../../../../components';
import { useAppSelector } from '../../../../contexts/hooks';

interface ModalConfigureLocationProps {
  isModalVisible: boolean;
  setIsModalVisible: (visible: boolean) => void;
  userId: number | null;
}

const ModalConfigureLocationComponent = ({ isModalVisible, setIsModalVisible, userId }: ModalConfigureLocationProps) => {
  const [loading, setLoading] = useState(false);
  const [locations, setLocations] = useState<any[]>([]);
  const { isMedium, isSmall, isXSmall } = useScreenSize();
  const { GetLocationByUser, AddLocationToUser, DeleteLocationFromUser } = useApiConfigUsuarios();
  const SysI_Sedes = useAppSelector((x) => x.configuration.Data?.ComboBox.filter((y) => y.Name === 'SysI_Sedes')[0]?.Data);
  // Ref para evitar múltiples fetchs mientras la modal está abierta
  const lastFetchedUserId = useRef<number | null>(null);
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  useEffect(() => {
    // Solo hacemos la petición si la modal está visible y cambió el userId
    if (isModalVisible) {
      if (userId && lastFetchedUserId.current !== userId) {
        setLoading(true);
        GetLocationByUser(userId)
          .then((res) => {
            const data = Array.isArray(res) ? res : res ? [res] : [];
            if (mounted.current) setLocations(data);
            lastFetchedUserId.current = userId;
          })
          .catch((err) => {
            console.error('Error fetching locations', err);
            newToast('ERROR', 'Error al obtener sedes del usuario', 3);
            if (mounted.current) setLocations([]);
          })
          .finally(() => {
            if (mounted.current) setLoading(false);
          });
      }
    } else {
      // Cuando se cierra la modal resetear estado para la próxima apertura
      setLocations([]);
      lastFetchedUserId.current = null;
    }
    // intentionally not depending on unstable function refs beyond GetLocationByUser
  }, [isModalVisible, userId, GetLocationByUser]);

  const refreshLocations = useCallback(() => {
    if (!userId) return;
    setLoading(true);
    GetLocationByUser(userId)
      .then((res) => {
        const data = Array.isArray(res) ? res : res ? [res] : [];
        if (mounted.current) setLocations(data);
      })
      .catch(() => {
        if (mounted.current) setLocations([]);
      })
      .finally(() => {
        if (mounted.current) setLoading(false);
      });
  }, [userId, GetLocationByUser]);

  const handleClose = useCallback(() => {
    setIsModalVisible(false);
  }, [setIsModalVisible]);

  return (
    <Popup visible={isModalVisible} onHiding={handleClose} maxHeight={'90%'} dragEnabled={false} showCloseButton={true} showTitle={true} title={`Sedes del usuario ${userId ?? ''}`} width={700} fullScreen={isMedium || isSmall || isXSmall} height={'auto'}>
      <ToolbarItem
        widget='dxButton'
        toolbar='bottom'
        location='after'
        options={{
          text: 'Cerrar',
          onClick: handleClose,
          stylingMode: 'outlined',
        }}
      />

      <div style={{ padding: 8 }}>
        {loading && (
          <div style={{ textAlign: 'center', marginBottom: 10 }}>
            <LoadIndicator visible={true} />
            <div>Cargando sedes...</div>
          </div>
        )}

        {!loading && (
          <div style={{ height: 300 }}>
            <DataGrid
              dataSource={locations}
              showBorders={true}
              repaintChangesOnly
              height={'100%'}
              onRowInserting={(e: any) => {
                let sdRaw = e.data?.Rus_Sd_Id;
                // Validar que no exista duplicado
                const existingIds = locations.filter((x) => x.Rus_Sd_Id == sdRaw);
                if (existingIds.length > 0) {
                  e.cancel = true;
                  newToast('ERROR', 'El usuario ya tiene asignada esa sede', 3);
                  return;
                }
              }}
              onRowInserted={async (e: any) => {
                // El valor seleccionado desde el lookup puede venir como número, string o como objeto
                let sdRaw = e.data?.Rus_Sd_Id;
                setLoading(true);
                const res = await AddLocationToUser(userId, sdRaw).catch(() => ({ success: false }));
                setLoading(false);
                if (res && res.success) {
                  // recargar lista
                  refreshLocations();
                } else {
                  newToast('ERROR', 'No se pudo agregar la sede', 3);
                  refreshLocations();
                }
              }}
              onRowRemoving={(e: any) => {
                // Validar que quede al menos una sede
                if (!locations || locations.length <= 1) {
                  newToast('ERROR', 'El usuario debe tener al menos una sede', 3);
                  e.cancel = true;
                }
              }}
              onRowRemoved={async (e: any) => {
                if (!userId) {
                  newToast('ERROR', 'Usuario inválido', 3);
                  refreshLocations();
                  return;
                }
                const sd = e.data?.Rus_Sd_Id;
                setLoading(true);
                const res = await DeleteLocationFromUser(userId, sd).catch(() => ({ success: false }));
                setLoading(false);
                if (res && res.success) {
                  refreshLocations();
                } else {
                  newToast('ERROR', 'No se pudo eliminar la sede', 3);
                  refreshLocations();
                }
              }}>
              <LoadPanel enabled={true} />
              <Column dataField={'Rus_Sd_Id'} caption={'SEDE'}>
                <Lookup dataSource={SysI_Sedes} valueExpr='Sd_Id' displayExpr='Sd_Nombre' />
              </Column>
              <Editing mode='row' allowUpdating={false} allowAdding={true} allowDeleting={true} />
            </DataGrid>
          </div>
        )}
      </div>
    </Popup>
  );
};

const ModalConfigureLocation = memo(ModalConfigureLocationComponent);
export default ModalConfigureLocation;
